import * as React from 'react';
import { t } from '@moovly/i18n';
import { formatDate } from '../../../utils/date';
import UsersStatusSelector from '../components/UsersStatusSelector';
import UsersTable, { TableUser } from '../components/UsersTable/UsersTable';
import RemoveMemberModal from '../modals/RemoveMemberModal';
import { UserData } from '../types/user';
import useUsersSearch from '../hooks/useUsersSearch';
import { useDispatch } from 'react-redux';
import { revokeLicense } from '../../subscriptions/store/licenses.store';
import { LicenseData } from '../../subscriptions/types/licenses';
import RevokeLicenseModal from '../modals/RevokeLicenseModal';
import { useModal } from '@moovly/ui';
import UserGroupFilter from './UserGroupFilter';
import { useSearchParams } from 'react-router-dom';

export enum UserPageTab {
  Active = 'assigned',
  Inactive = 'assigned-expired',
  Disabled = 'available',
}

const UsersContainer: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedGroup, setSelectedGroup] = React.useState<string | null>(searchParams.get('group'));

  React.useMemo(() => {
    setSelectedGroup(searchParams.get('group'));
  }, [searchParams]);
  const handleSetGroup = React.useCallback(
    (groupId: string | null) => {
      setSelectedGroup(groupId);
      setSearchParams((params) => {
        if (groupId) {
          params.set('group', groupId);
        } else {
          params.delete('group');
        }
        return params;
      });
    },
    [setSearchParams]
  );

  const { users, isLoading, isSearch } = useUsersSearch();
  const filteredUsers = React.useMemo(() => {
    if (!selectedGroup) {
      return users;
    }
    return users.filter((user) => {
      return user.memberships.some((memb) => memb.group_id === selectedGroup);
    });
  }, [users, selectedGroup]);
  const dispatch = useDispatch<any>();
  const {
    isOpen: removeMemberModalOpen,
    openModal: openRemoveMemberModal,
    modalProps: removeMemberModalUser,
    ...otherRemoveMemberProps
  } = useModal();

  const {
    isOpen: revokeLicenseModalOpen,
    openModal: openRevokeLicenseModal,
    modalProps: revokeMemberModalLicense,
    ...otherRevokeMemberModalProps
  } = useModal();

  const [currentTab, setTab] = React.useState<UserPageTab>(UserPageTab.Active);
  const groupedUsers = React.useMemo<{
    active: UserData[];
    disabled: UserData[];
    inactive: UserData[];
  }>(
    () => ({
      active: filteredUsers.filter((x) => x.license && !x.license.is_expired),
      inactive: filteredUsers.filter((x) => x.license && x.license.is_expired),
      disabled: filteredUsers.filter((x) => !x.license),
    }),
    [filteredUsers]
  );
  const count = React.useMemo<{
    [key in UserPageTab]: number;
  }>(
    () => ({
      [UserPageTab.Active]: isLoading ? 0 : groupedUsers.active.length,
      [UserPageTab.Disabled]: isLoading ? 0 : groupedUsers.disabled.length,
      [UserPageTab.Inactive]: isLoading ? 0 : groupedUsers.inactive.length,
    }),
    [groupedUsers, isLoading]
  );
  const selectedUsers = React.useMemo<UserData[]>(() => {
    if (isSearch) {
      return users;
    }
    switch (currentTab) {
      case UserPageTab.Active:
        return groupedUsers.active;
      case UserPageTab.Disabled:
        return groupedUsers.disabled;
      case UserPageTab.Inactive:
        return groupedUsers.inactive;
      default:
        return [...groupedUsers.active, ...groupedUsers.disabled, ...groupedUsers.inactive];
    }
  }, [currentTab, groupedUsers, users, isSearch]);
  const tableUsers = React.useMemo<TableUser[]>(
    () =>
      selectedUsers.map((user) => {
        return {
          uuid: user.id,
          name: `${user.name || ''}${user.name && user.first_name ? ' ' : ''}${user.first_name || ''}`.trim(),
          emailAddress: user.email,
          lastLoginAt: user.last_login_at,
          projectCount: user.projectCount || 0,
          licenseName: user.license?.contract.license_type,
          licenseExpiresAt: formatDate(user.license?.expires_at!) || undefined,
          role: user.memberships.find((m) => m.role === 'admin') ? t('roles.admin') : t('roles.user'),
          license: user.license,
          memberId: '',
        };
      }),
    [selectedUsers]
  );

  const handleRevokeLicense = React.useCallback(
    (license: LicenseData) => {
      if (!license.is_expired) {
        openRevokeLicenseModal(license);
      } else {
        dispatch(
          revokeLicense({
            contractId: license.contract.id,
            licenseId: license.id,
          })
        );
      }
    },
    [dispatch, openRevokeLicenseModal]
  );

  return (
    <>
      <>
        {!isSearch && (
          <div className="d-flex justify-content-between align-items-center mb-3">
            <UsersStatusSelector status={currentTab} count={count} onSelectStatus={setTab} />
            <UserGroupFilter onChange={handleSetGroup} selectedGroup={selectedGroup} />
          </div>
        )}
        <UsersTable
          isLoading={isLoading}
          users={tableUsers}
          onRemoveMember={openRemoveMemberModal}
          onRevoke={handleRevokeLicense}
        />
        {removeMemberModalOpen && <RemoveMemberModal user={removeMemberModalUser} {...otherRemoveMemberProps} />}
        {revokeLicenseModalOpen && (
          <RevokeLicenseModal license={revokeMemberModalLicense} {...otherRevokeMemberModalProps} />
        )}
      </>
    </>
  );
};
export default UsersContainer;
